import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Button, Alert, Spinner, Card, Table, Pagination } from 'flowbite-react';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/ToastProvider';

export default function Apps() {
  const { addToast } = useToast();
  const [apps, setApps] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [action, setAction] = useState({ type: null, id: null });

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(apps.length / pageSize));
  const pageItems = useMemo(() => apps.slice((page - 1) * pageSize, page * pageSize), [apps, page]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/apps');
      setApps(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const importFixtures = async (id) => {
    setAction({ type: 'import', id });
    try {
      await api.post(`/apps/${id}/import-fixtures`, {});
      await load();
      addToast({ type: 'info', message: 'Фикстуры импортированы' });
    } catch (e) {
      setError(e.message);
    } finally {
      setAction({ type: null, id: null });
    }
  };

  return (
    <div className="grid gap-4">
      <ConfirmModal open={confirm.open} title={confirm.title} message={confirm.message} onClose={() => setConfirm({ open: false })} onConfirm={confirm.onConfirm} />

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Мои приложения Realgram</h2>
            <p className="text-sm text-gray-500 mt-1">Управление ботами и веб-приложениями недвижимости</p>
          </div>
          <Button as={Link} to="/apps/new" color="blue">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Добавить приложение
          </Button>
        </div>
        {error && <Alert color="failure" className="mt-2">{error}</Alert>}
        {loading ? (
          <div className="py-4"><Spinner /></div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="grid gap-3 sm:hidden">
              {pageItems.length === 0 ? (
                <div className="text-sm text-gray-500">Пока нет приложений</div>
              ) : (
                pageItems.map((a) => {
                  const isImporting = action.type === 'import' && action.id === a.id;
                  return (
                    <Card key={a.id}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Link className="font-semibold underline underline-offset-2 decoration-blue-500 hover:text-blue-700" to={`/${a.id}/dashboard`}>{a.name}</Link>
                          <div className="text-xs text-gray-500 break-all mt-1">{a.id}</div>
                        </div>
                        <div className="text-right text-sm">
                          <div>Пользователи: <span className="font-semibold">{a.users}</span></div>
                          <div>Объекты: <span className="font-semibold">{a.properties}</span></div>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Button color="light" size="xs" onClick={() => importFixtures(a.id)} disabled={isImporting}>
                          {isImporting ? (<span className="inline-flex items-center gap-1"><Spinner size="sm" /> Импорт...</span>) : 'Импорт фикстур'}
                        </Button>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>

            {/* Desktop table */}
            <div className="overflow-x-auto hidden sm:block">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>Название</Table.HeadCell>
                  <Table.HeadCell className="hidden sm:table-cell">UUID</Table.HeadCell>
                  <Table.HeadCell>Пользователи</Table.HeadCell>
                  <Table.HeadCell>Объекты</Table.HeadCell>
                  <Table.HeadCell>
                    <span className="sr-only">Действия</span>
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {pageItems.map((a) => {
                    const isImporting = action.type === 'import' && action.id === a.id;
                    return (
                      <Table.Row key={a.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                        <Table.Cell className="align-top">
                          <Link className="font-semibold underline underline-offset-2 decoration-blue-500 hover:text-blue-700 break-words max-w-[180px] sm:max-w-none inline-block" to={`/${a.id}/dashboard`}>{a.name}</Link>
                          <div className="sm:hidden text-xs text-gray-500 break-all mt-1">{a.id}</div>
                        </Table.Cell>
                        <Table.Cell className="hidden sm:table-cell font-mono break-all text-xs text-gray-600 align-top">{a.id}</Table.Cell>
                        <Table.Cell className="whitespace-nowrap align-top">{a.users}</Table.Cell>
                        <Table.Cell className="whitespace-nowrap align-top">{a.properties}</Table.Cell>
                        <Table.Cell className="align-top">
                          <div className="flex flex-wrap gap-2 justify-end">
                            <Button color="light" size="xs" onClick={() => importFixtures(a.id)} disabled={isImporting}>
                              {isImporting ? (<span className="inline-flex items-center gap-1"><Spinner size="sm" /> Импорт...</span>) : 'Импорт фикстур'}
                            </Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                  {pageItems.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan={5} className="text-center text-sm text-gray-500">Пока нет приложений</Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
            <div className="mt-3 flex justify-end">
              <Pagination currentPage={page} onPageChange={setPage} totalPages={totalPages} showIcons />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}